/* eslint no-bitwise: 0 */
import DraftOffsetKey from 'draft-js/lib/DraftOffsetKey';
import { EditorState } from 'draft-js';
import { getCurrentBlockText } from './blockUtilities';
import { ENTITY_TYPE } from './CONSTANTS';

const getSelectionRange = () => {
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return null;
    return selection.getRangeAt(0);
};

const getSelectedBlockElement = (range) => {
    let node = range.startContainer;
    do {
        const nodeIsDataBlock = node.getAttribute
            ? node.getAttribute('data-block')
            : null;
        if (nodeIsDataBlock) return node;
        node = node.parentNode;
    } while (node !== null);
    return null;
};

const getSelectedLink = (editorState) => {
    const contentState = editorState.getCurrentContent();
    let currentBlock = editorState
        .getCurrentContent()
        .getBlockForKey(editorState.getSelection().getStartKey());
    const endBlock = editorState
        .getCurrentContent()
        .getBlockForKey(editorState.getSelection().getEndKey());
    let currentEntityKey;
    let offset = editorState.getSelection().getStartOffset();
    while (currentBlock && offset < editorState.getSelection().getEndOffset() && offset < currentBlock.getLength() - 1) {
        currentEntityKey = currentBlock.getEntityAt(offset);
        if (currentEntityKey && contentState.getEntity(currentEntityKey).type.toUpperCase() === ENTITY_TYPE.LINK) {
            return {
                text: '',
                link: contentState.getEntity(currentEntityKey).data.url,
            };
        }
        offset += 1;
        if (currentBlock.getKey() !== endBlock.getKey && offset === currentBlock.getLength()) {
            currentBlock = editorState.getCurrentContent().getBlockAfter(currentBlock.getKey());
            offset = 0;
        }
    }
    return null;
};

const getWordAt = (string, position) => {
    const str = String(string);
    const pos = Number(position) >>> 0;
    const left = str.slice(0, pos + 1).search(/\S+$/);
    const right = str.slice(pos).search(/\s/);
    if (right < 0) {
        return {
            word: str.slice(left),
            begin: left,
            end: str.length,
        };
    }
    return {
        word: str.slice(left, right + pos),
        begin: left,
        end: right + pos,
    };
};

const getSearchText = (editorState, selection) => {
    const anchorKey = selection.getAnchorKey();
    const anchorOffset = selection.getAnchorOffset() - 1;
    const currentContent = editorState.getCurrentContent();
    const currentBlock = currentContent.getBlockForKey(anchorKey);
    const blockText = currentBlock.getText();
    return getWordAt(blockText, anchorOffset);
};

const isMultilineSelection = (editorState) => {
    const selection = editorState.getSelection();
    const startKey = selection.getStartKey();
    const startOffset = selection.getStartOffset();
    const endKey = selection.getEndKey();
    const endOffset = selection.getEndOffset();

    const currentContent = editorState.getCurrentContent();
    const text = getCurrentBlockText(editorState).slice(startOffset, endOffset);

    const blockAfterStart = currentContent.getBlockAfter(startKey);
    const blockAfterStartKey = blockAfterStart && blockAfterStart.getKey();

    const isMultipleLines = text.trim().split('\n').length > 1;
    const singleLineMultipleBlocksCase = (endKey === blockAfterStartKey) && (endOffset === 0);
    const isMultipleBlocks = (startKey !== endKey) && !singleLineMultipleBlocksCase;

    return isMultipleLines || isMultipleBlocks;
};

const textIsSelected = editorState => !editorState.getSelection().isCollapsed();

const getCurrentNode = (editorState) => {
    const selection = editorState.getSelection();
    const currentContent = editorState.getCurrentContent();
    const currentBlock = currentContent.getBlockForKey(selection.getStartKey());
    const offsetKey = DraftOffsetKey.encode(currentBlock.getKey(), 0, 0);

    return document.querySelectorAll(`[data-offset-key="${offsetKey}"]`)[0];
};

const getBlockPosition = (editorState) => {
    const node = getCurrentNode(editorState);
    if (node != null) {
        const boundingRect = node.getBoundingClientRect();
        const scrollY = window.scrollY == null ? window.pageYOffset : window.scrollY;
        const top = boundingRect.bottom + scrollY;

        return { left: boundingRect.left, top };
    }

    return null;
};

const moveSelectionToStart = (editorState) => {
    const startKey = editorState.getCurrentContent().getBlockMap().first().getKey();

    return EditorState.forceSelection(
        editorState,
        editorState.getSelection().merge({
            anchorKey: startKey,
            focusKey: startKey,
            anchorOffset: 0,
            focusOffset: 0,
        }),
    );
};

export {
    getBlockPosition,
    getCurrentNode,
    getSelectionRange,
    getSelectedBlockElement,
    getSelectedLink,
    getSearchText,
    getWordAt,
    isMultilineSelection,
    textIsSelected,
    moveSelectionToStart,
};
